import { PageFormToolbar } from '../../../components-common/layout/views/PageFormToolbar';
import { duplicateTableRow } from '../../../components-common/_shared/actionButton.helper';
import { IndexContainer } from '../../../components-common/containers/IndexContainer';
import {
  getParameterValidationSchema,
  StructureDataParameterValidationSchema,
} from '../../../components-domain/bim-domain/parameters/parameter.schema';
import { StructureDataParameterForm } from '../../../components-domain/bim-domain/parameters/StructureDataParameterForm';
import { BuildingParameterForm } from '../../../components-domain/bim-domain/parameters/BuildingParameterForm';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

/**
 * Function: parameterFormScreen
 *
 * This function generates a screen configuration object for a parameter form within a given category.
 * The form is used for creating or editing parameters with validation and initial empty values.
 *
 * @param {string} category - The category for which the parameter form screen is created.
 *
 * Returns:
 * @returns {Object} A screen configuration object with properties for the parameter form, including component, form structure, validation schema, and initial empty values.
 */
const parameterFormScreen = (category: string) => {
  return {
    Component: PageFormToolbar,
    form: BuildingParameterForm,
    template: '/buildingms/categories/parameters',
    schema: getParameterValidationSchema(),
    empty: {
      ...{
        creationType: 'CONSTANT',
        type: 'TEXT',
        name: '',
        value: '',
        description: '',
        category,
        min: 0,
        max: 2,
      },
    },
  };
};

export const toolbar_components = {
  id: 'table_components',
  cat: 'toolbar',
  type: 'index',
  left: ['create_new'],
  right: ['back'],
};

/**
 * Function: parametersTab
 *
 * This function generates a tab configuration object for displaying and managing parameters of a given category.
 *
 * @param {string} category - The category for which the parameters tab is created.
 *
 * Returns:
 * @returns {Object} A tab configuration object with an id, title, and a list of components for the parameters tab.
 */
export const parametersTab = (category: string) => {
  return {
    id: `${category}_params`,
    title: 'Parameters',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            ...toolbar_components,
            left: [
              {
                id: 'create',
                kind: 'create',
                onAction: (props: any) => props.navigate('parameters/new'),
              } as any,
            ],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'table_components',
            props: {
              source: {
                type: 'REMOTE',
                url: '/buildingms/categories/parameters',
                key: '/buildingms/categories/parameters',
                urlParams: { category },
              },
              columns: [
                'name',
                'type',
                { id: 'sourceType' },
                { id: 'valueDefault', dataField: 'value' },
                'min',
                'max',
                'description',
              ],
              actions: [
                {
                  id: 'edit',
                  onClick: (props: any) =>
                    props.navigate(`parameters/${props.row.id}`),
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Edit parameter',
                },
                {
                  id: 'copy',
                  actionToDispatch: {
                    payload: {
                      copy: '/buildingms/categories/parameters/cloneParameter',
                    },
                  },
                  displayTooltip: true,
                  tooltipText: 'Duplicate parameter',
                },
                {
                  id: 'delete',
                  actionToDispatch: {
                    payload: {
                      endpointToDelete: '/buildingms/categories/parameters',
                    },
                  },
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Delete parameter',
                },
              ],
            },
          },
        },
      },
    ],
  };
};

/**
 * Function: newParameterScreen
 *
 * This function generates a screen configuration object for creating a new parameter in a given category.
 *
 * @param {string} category - The category for which the new parameter screen is created.
 *
 * Returns:
 * @returns {Object} A screen configuration object with properties for the new parameter creation screen.
 */
export const newParameterScreen = (category: string) => {
  return {
    id: 'create',
    path: 'parameters/new',
    title: `Create ${category} Parameter`,
    tmp: 'create',
    left: ['create'],
    right: ['back'],
    roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
    ...parameterFormScreen(category),
  };
};

/**
 * Function: editParameterScreen
 *
 * This function generates a screen configuration object for editing an existing parameter in a given category.
 *
 * @param {string} category - The category for which the edit parameter screen is created.
 *
 * Returns:
 * @returns {Object} A screen configuration object with properties for the edit parameter screen.
 */
export const editParameterScreen = (category: string) => {
  return {
    id: 'edit',
    tmp: 'edit',
    title: `Edit ${category} Parameter`,
    path: 'parameters/:parameterId',
    left: ['save'],
    right: ['back'],
    editSource: '/buildingms/categories/parameters/',
    idPropSource: 'parameterId',
    roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
    ...parameterFormScreen(category),
  };
};
